import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import type { AxiosError } from "axios";
import KalixLogo from "../../components/svgElements/KalixLogo";
import InputWrapper from "../../components/formElements/InputWrapper";
import Textbox from "../../components/formElements/Textbox";
import Button from "../../components/formElements/Button";
import { ROUTES } from "../../constants/routes";
import { resetPassword } from "../../services/auth";
import { showErrorToast, showSuccessToast } from "../../utils/helper";
import "./Auth.scss";

interface IResetPasswordForm {
  password: string;
  confirmPassword: string;
}

const currentYear = new Date().getFullYear();

const ResetPassword = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(false);

  const email = location.state?.email;
  const code = location.state?.code;

  const {
    control,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<IResetPasswordForm>({
    mode: "onSubmit",
    defaultValues: {
      password: "",
      confirmPassword: "",
    },
  });

  const password = watch("password");

  useEffect(() => {
    if (!email || !code) {
      showErrorToast(
        "Reset token not found. Please start from forgot password."
      );
      navigate(ROUTES.FORGOT_PASSWORD);
    }
  }, [email, code, navigate]);

  const onSubmit = async (data: IResetPasswordForm) => {
    try {
      setIsLoading(true);
      const response = await resetPassword(email, code, data.password);

      if (response.success) {
        showSuccessToast(response.message || t("auth.resetPasswordSuccess"));
        navigate(ROUTES.LOGIN);
      } else {
        showErrorToast(response.message || t("auth.resetPasswordFailed"));
      }
    } catch (error) {
      const axiosError = error as AxiosError<{ message?: string }>;
      const errorMessage =
        axiosError?.response?.data?.message ||
        axiosError?.message ||
        t("auth.resetPasswordError");
      showErrorToast(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };
  return (
    <div className="userauth-main">
      <div className="row g-3">
        <div className="col-md-7">
          <div className="userauth-bg" />
        </div>

        <div className="col-md-5">
          <div className="userauth-form">
            <div className="userauth-logo">
              <KalixLogo />
            </div>
            <div className="userauth-form-content">
              <h2 className="text-center">{t("auth.resetPassword")}</h2>
              <p className="text-center">
                {t("auth.resetPasswordDescription")}
              </p>
              <form onSubmit={handleSubmit(onSubmit)}>
                <InputWrapper>
                  <InputWrapper.Label required>
                    {t("auth.newPassword")}
                  </InputWrapper.Label>
                  <Textbox
                    type="password"
                    name="password"
                    placeholder={t("auth.enterNewPassword")}
                    control={control}
                    className="form-control"
                    icon={true}
                    showPassword={true}
                    rules={{
                      required: t("validation.newPasswordRequired"),
                      minLength: {
                        value: 8,
                        message: t("validation.passwordMinLength", { count: 8 }),
                      },
                    }}
                  />
                  {errors.password && (
                    <span className="auth-msg error">
                      {errors.password.message}
                    </span>
                  )}
                </InputWrapper>

                <InputWrapper>
                  <InputWrapper.Label required>
                    {t("auth.confirmNewPassword")}
                  </InputWrapper.Label>
                  <Textbox
                    type="password"
                    name="confirmPassword"
                    placeholder={t("auth.reEnterNewPassword")}
                    control={control}
                    className="form-control"
                    icon={true}
                    showPassword={true}
                    rules={{
                      required: t("validation.confirmPasswordRequired"),
                      validate: (value: string) =>
                        value === password || t("validation.passwordsDoNotMatch"),
                    }}
                  />
                  {errors.confirmPassword && (
                    <span className="auth-msg error">
                      {errors.confirmPassword.message}
                    </span>
                  )}
                </InputWrapper>

                <Button
                  type="submit"
                  className="primary-btn rounded-md w-100"
                  disabled={isLoading}
                >
                  {isLoading ? t("common.loading") : t("auth.resetPassword")}
                </Button>

                <div className="button-align justify-content-center mt-5">
                  <p className="color-dark">
                    {t("auth.rememberPassword")}{" "}
                    <Link to={ROUTES.LOGIN} className="color-primary">
                      {t("auth.signInLink")}
                    </Link>
                  </p>
                </div>
              </form>
            </div>
            <p className="copyright-text">
              {t("auth.copyright", { year: currentYear })}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
